'use client';
import React from 'react';
import Link from 'next/link';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const perks = [
  'Free to create and share',
  'No credit card required',
  'Ready in under 30 minutes',
  'Print-ready PDF available',
];

export default function CtaSection() {
  const { ref, isVisible } = useScrollReveal({ threshold: 0.15 });

  return (
    <section id="pricing" className="py-24 relative overflow-hidden">
      {/* Vivid background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] blur-3xl" style={{ background: 'radial-gradient(ellipse, rgba(245,158,11,0.12) 0%, rgba(139,92,246,0.1) 50%, transparent 70%)' }} />
        <div className="absolute top-0 right-0 w-72 h-72 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(236,72,153,0.1) 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 left-0 w-72 h-72 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(14,165,233,0.08) 0%, transparent 70%)' }} />
      </div>

      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 text-center relative">
        <div className="max-w-2xl mx-auto">
          {/* Panel */}
          <div
            ref={ref}
            className={`p-10 md:p-14 relative ${isVisible ? 'reveal-scale delay-0' : 'reveal-hidden'}`}
            style={{ background: 'linear-gradient(135deg, rgba(30,34,53,0.9) 0%, rgba(37,42,64,0.9) 100%)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
          >
            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg, transparent, #f59e0b, #f97316, transparent)' }} />

            <h2 className={`text-4xl md:text-5xl font-extrabold text-zinc-100 mb-4 ${isVisible ? 'reveal-up delay-100' : 'reveal-hidden'}`}>
              Your next production deserves
              <span className="text-gradient-gold"> a beautiful program.</span>
            </h2>
            <p className={`text-zinc-400 text-lg mb-8 ${isVisible ? 'reveal-up delay-200' : 'reveal-hidden'}`}>
              Join 380+ schools and theatres who use Ovation Programs to create stunning digital Playbills.
            </p>

            <div className={`flex flex-wrap justify-center gap-4 mb-10 ${isVisible ? 'reveal-fade delay-300' : 'reveal-hidden'}`}>
              {perks?.map((perk) => (
                <div key={`perk-${perk}`} className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle size={14} className="text-gold-400" />
                  {perk}
                </div>
              ))}
            </div>

            <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 ${isVisible ? 'reveal-up delay-400' : 'reveal-hidden'}`}>
              <Link
                href="/sign-up-login-screen"
                className="btn-gold group flex items-center gap-2 font-bold px-10 py-4 text-base"
                style={{ background: 'linear-gradient(135deg, #f59e0b, #f97316)', color: '#0f0f1a', borderRadius: '6px', boxShadow: '0 0 40px rgba(245,158,11,0.35)' }}
              >
                Create Your First Program
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <p className={`text-zinc-600 text-xs mt-6 ${isVisible ? 'reveal-fade delay-500' : 'reveal-hidden'}`}>
              PDF export is $50 per program. Flipbook sharing is always free.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}